<template>
  <div class="my-8 w-full xl:max-w-[650px]">
    <div
      class="relative z-10 w-full overflow-hidden rounded-xl border-l-[12px] border-blue bg-white px-4 py-6 shadow-[0_0_84px_0_rgba(0,180,210,0.23)] lg:mx-0 lg:rounded-2xl lg:border-l-[16px] lg:p-9 xl:mt-0 xl:max-w-[650px]"
    >
      <!-- Toggle inside the box -->
      <div v-if="showToggle" class="mb-4 flex w-full rounded-full border-2 border-blue bg-white">
        <button
          v-for="(tab, i) in toggleTabs"
          :key="i"
          @click="$emit('toggle-change', i)"
          :class="[
            'flex-1 rounded-full px-4 py-2 text-sm font-bold lg:text-base',
            selectedToggleTab === i ? ' bg-blue text-white' : 'bg-white text-blue',
          ]"
        >
          {{ tab }}
        </button>
      </div>

      <SubTitle class="text-black lg:text-3xl lg:leading-none">
        {{
          selectedToggleTab === 1
            ? $t('donation-monthly.subtitle')
            : $t('donation-standard.subtitle')
        }}
      </SubTitle>
      <Title custom-style="!text-6xl uppercase lg:!leading-none">
        {{ sumModel }} {{ currencySymbol }}{{ selectedToggleTab === 1 ? `${$t('per-month')}` : '' }}
      </Title>

      <div class="mt-4">
        <p class="my-4 font-sans text-base lg:block">{{ $t('donation-standard.text') }}</p>
        <div class="flex space-x-2 lg:space-x-4">
          <Button
            v-for="(suma, index) in summa"
            :key="index"
            :title="`${suma.value} ${currencySymbol}`"
            class="flex-1 !py-2 lowercase lg:!py-2 lg:leading-none"
            class-name-text="text-sm lg:text-base "
            :type="sumModel === suma.value ? undefined : 'outline'"
            @click="handleClickSum(suma.value)"
          />
          <Button
            :title="$t('sum.other')"
            class="flex-1 !py-2 lowercase lg:!py-2 lg:leading-none"
            class-name-text="text-sm lg:text-base "
            :type="otherSum ? undefined : 'outline'"
            @click="handleClickOther"
          />
        </div>

        <Input
          v-show="otherSum"
          v-model.number="sumModel"
          type="number"
          :min="minSum"
          :label="$t('choose-donation-sum')"
          style-input="w-full border !border-blue-dark focus:border-blue-dark shadow-[0_12px_33px_0_rgba(5, 58, 65, 0.04)] text-blue text-sm lg:text-base font-bold !pr-10 w-40"
          style-input-wrapper="w-40"
          :currency="currencySymbol"
          class="mt-6"
          :right="otherRightString"
          :v$="v$"
        />
      </div>
      <Button
        :title="buttonLabel ?? $t('button.next')"
        :arrow-right="buttonArrow ?? true"
        color="pink"
        class="mt-8 w-full lg:w-[370px]"
        @click="handleClickNext"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import type { Validation } from '@vuelidate/core'
import { useVModel } from '@vueuse/core'
import { Input, Button, Title, SubTitle } from 'refresh-ui'
import { useI18n } from 'vue-i18n'
import type { SiteLang, ApproxCurrency, Currency } from 'refresh-ui'

const config = useRuntimeConfig()

const { SITE_LANG } = config.public as {
  SITE_LANG: SiteLang
}

const { t } = useI18n()

const props = defineProps<{
  sum: number
  minSum: number
  currency: Currency
  sums: number[]
  v$: Validation
  title?: string
  image?: string
  showToggle?: boolean
  toggleTabs?: string[]
  selectedToggleTab?: number
  buttonLabel?: string
  buttonArrow?: boolean
}>()

const isMultiCurrency = false // Move to env var?

const baseCurrency = computed<Currency>(() =>
  props?.currency ? props.currency : SITE_LANG === 'sv' ? 'sek' : 'eur',
)

const currencySymbolMap: Record<Currency, string> = {
  sek: 'kr',
  eur: '€',
  usd: '$',
} as const

const langApproxCurrencyMap = {
  sv: 'sek',
  en: 'eur',
  uk: 'uah',
  es: 'eur',
  fi: 'eur',
} as const

const currencyMapBasedOnSek: Record<ApproxCurrency, number> = {
  sek: 1,
  eur: 0.089,
  usd: 0.095,
  uah: 3.76,
} as const

const currencyMapBasedOnEur: Record<ApproxCurrency, number> = {
  sek: 11.2,
  eur: 1,
  usd: 1.06,
  uah: 42.42,
} as const

const approxCurrency = computed<ApproxCurrency>(() => langApproxCurrencyMap[SITE_LANG])

const approxCurrencyConversion = (sum: number): number => {
  return baseCurrency.value === 'eur'
    ? currencyMapBasedOnEur[approxCurrency.value] * sum
    : currencyMapBasedOnSek[approxCurrency.value] * sum
}

const currencySymbol = computed(() => currencySymbolMap[baseCurrency.value])

const approx = computed(() => approxCurrencyConversion(props.sum))

const otherRightString = `<span class='absolute right-4 bottom-3 lg:bottom-4 text-sm font-bold text-blue lg:text-base'>${currencySymbol.value}</span>`

const emits = defineEmits(['update:sum', 'onNext', 'toggle-change'])

const sumModel = useVModel(props, 'sum', emits)

const summa = computed(() =>
  props.sums.map(summa => ({
    value: summa,
    currency: isMultiCurrency ? approxCurrency : baseCurrency,
  })),
)

const otherSum = ref(summa.value.some(sum => sum.value === props.sum) ? false : true)

const handleClickNext = () => {
  if (props.v$) {
    props.v$.$touch()
    props.v$.$validate()

    if (!props.v$.$invalid) emits('onNext')
    return
  }
  emits('onNext')
}

const handleClickSum = (suma: any) => {
  sumModel.value = suma
  otherSum.value = false
}
const handleClickOther = () => {
  otherSum.value = true
  // Lets leave the sum as is, don't change it to minSum
  // sumModel.value = props.minSum
}
</script>
